import { useState } from 'react';
import {
  Workflow as WorkflowIcon,
  Plus,
  Sparkles,
  FileText,
  ArrowRight,
  Trash2,
  Copy,
  Search,
} from 'lucide-react';
import type { WorkflowStatus } from '@pm-next/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useWorkflowTemplates } from '@/hooks/useWorkflows';

export interface WorkflowListItem {
  id: string;
  name: string;
  status: WorkflowStatus;
  description?: string;
}

export interface WorkflowHomeViewProps {
  workflows: WorkflowListItem[];
  isLoading?: boolean;
  search: string;
  statusFilter: string;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onOpenWorkflow: (id: string) => void;
  onCreateWorkflow: () => void;
  onUseTemplate: (templateId: string) => void;
  onDeleteWorkflow: (id: string) => void;
  onDuplicateWorkflow: (id: string) => void;
  onGenerateWorkflow: (description: string) => void;
  isGenerating?: boolean;
}

const STATUS_BADGE: Record<WorkflowStatus, string> = {
  draft: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  active: 'bg-primary/15 text-primary',
  inactive: 'bg-muted text-muted-foreground',
  archived: 'bg-muted text-muted-foreground line-through',
};

const STATUS_LABEL: Record<WorkflowStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
};

export function WorkflowHomeView({
  workflows,
  isLoading = false,
  search,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
  onOpenWorkflow,
  onCreateWorkflow,
  onUseTemplate,
  onDeleteWorkflow,
  onDuplicateWorkflow,
  onGenerateWorkflow,
  isGenerating = false,
}: WorkflowHomeViewProps) {
  const { toast } = useToast();
  const [aiPrompt, setAiPrompt] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const { data: templates = [], isLoading: templatesLoading } = useWorkflowTemplates();

  const confirmDeleteWorkflow = workflows.find((w) => w.id === confirmDeleteId) ?? null;

  const handleGenerateWithAI = () => {
    if (!aiPrompt.trim()) {
      toast({
        variant: 'destructive',
        title: 'Enter a description',
        description: 'Describe the workflow you want to generate.',
      });
      return;
    }
    onGenerateWorkflow(aiPrompt.trim());
  };

  const handleConfirmDelete = () => {
    if (confirmDeleteId) {
      onDeleteWorkflow(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-8 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WorkflowIcon className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Workflows</h1>
          </div>
          <Button onClick={onCreateWorkflow} className="gap-2">
            <Plus className="h-4 w-4" />
            Create workflow
          </Button>
        </div>

        {/* Existing workflows */}
        <section>
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-sm font-medium text-foreground">Your workflows</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="h-8 w-[200px] pl-8 text-sm"
                  placeholder="Search workflows"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
              <Select value={statusFilter || 'all'} onValueChange={onStatusFilterChange}>
                <SelectTrigger className="h-8 w-[130px] text-sm">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {isLoading ? (
            <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">Loading workflows…</p>
            </div>
          ) : workflows.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">No workflows found</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Create one from scratch or start from a template below
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {workflows.map((w) => (
                <li key={w.id}>
                  <div
                    className={cn(
                      'flex w-full items-center justify-between gap-2 rounded-lg border bg-card px-4 py-3 transition-colors',
                      'hover:border-primary/40 hover:bg-accent/50'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onOpenWorkflow(w.id)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground">{w.name}</p>
                        {w.description && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {w.description}
                          </p>
                        )}
                      </div>
                    </button>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-xs',
                        STATUS_BADGE[w.status]
                      )}
                    >
                      {STATUS_LABEL[w.status]}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicateWorkflow(w.id);
                      }}
                      aria-label="Duplicate workflow"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteId(w.id);
                      }}
                      aria-label="Delete workflow"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Templates from API */}
        <section>
          <h2 className="mb-3 text-sm font-medium text-foreground">Start from a template</h2>
          {templatesLoading ? (
            <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-6 text-center">
              <p className="text-sm text-muted-foreground">Loading templates…</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => onUseTemplate(template.id)}
                  className={cn(
                    'group flex items-start gap-3 rounded-xl border bg-card p-4 text-left transition-all',
                    'hover:border-primary/50 hover:bg-accent/50'
                  )}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-foreground">{template.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{template.description}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Generate with AI */}
        <section>
          <h2 className="mb-3 text-sm font-medium text-foreground">Generate workflow using AI</h2>
          <div className="rounded-xl border bg-card p-4">
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <Textarea
                  placeholder="e.g. When payroll is finalized, check for anomalies, summarize with AI, and notify the finance manager"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  disabled={isGenerating}
                  className="min-h-[80px] resize-none text-sm"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  className="gap-2"
                  onClick={handleGenerateWithAI}
                  disabled={isGenerating}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {isGenerating ? 'Generating…' : 'Generate workflow'}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Dialog open={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete workflow?</DialogTitle>
            <DialogDescription>
              {confirmDeleteWorkflow
                ? `"${confirmDeleteWorkflow.name}" and all its run history will be permanently deleted. This cannot be undone.`
                : 'This workflow and all its run history will be permanently deleted.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScrollArea>
  );
}
